// URL builder for the bin+SKU flag endpoint (issue #3986).

/** The pick task fields that identify one bin+SKU entry. */
export interface BinSkuFlagTarget {
  binId?: string | null;
  skuId?: string | null;
  partition?: string | null;
  batchId?: string | null;
  caseId?: string | null;
}

const trimmed = (value: unknown): string => String(value ?? "").trim();

/**
 * Path for flagging (or unflagging) one SKU's stock inside one bin.
 *
 * Returns null when the pick task carries no bin or SKU id — the caller must
 * treat that as a flag failure rather than fall back to the SKU-wide endpoint,
 * which is the behaviour #3986 exists to remove.
 */
export const buildFlagBinSkuUrl = (
  target: BinSkuFlagTarget | null | undefined,
  remarks: string,
  isFlagged: boolean = true
): string | null => {
  const binId = trimmed(target?.binId);
  const skuId = trimmed(target?.skuId);

  if (!binId || !skuId) {
    return null;
  }

  const path =
    `/BinMaster/flagBinSku/${encodeURIComponent(binId)}/${encodeURIComponent(
      skuId
    )}/${isFlagged}/${encodeURIComponent(remarks)}`;

  // Blank optionals are omitted rather than sent empty: FlagBinSkuAsync treats a
  // null/empty value as "don't narrow on this field", and an empty query value
  // would arrive as "" and mean the same thing, so sending it is just noise.
  const query = new URLSearchParams();
  const batchId = trimmed(target?.batchId);
  const partition = trimmed(target?.partition);
  const caseId = trimmed(target?.caseId);

  if (batchId) {
    query.append("batchId", batchId);
  }
  if (partition) {
    query.append("partition", partition);
  }
  if (caseId) {
    query.append("caseId", caseId);
  }

  const queryString = query.toString();

  return queryString ? `${path}?${queryString}` : path;
};
